import { uniqBy } from "lodash";

import { IMappedBankAccount } from "types/BankAccounts";
import { BankAccountVerifyStatus } from "types/BankAccountVerifyStatus";
import { PayDistributionType } from "types/BETypes";
import { CardForm, CardFulfillmentStatus, CardStatus, CardStatusReason } from "types/CardShipping";

import {
  BankCardResDto,
  GetPlaidBankAccountsResponseDto,
  SyncteraAccountDto,
} from "utils/swagger_react_query";

export const getSortedAndFilteredPhysicalBankCards = (cards: BankCardResDto[]) => {
  let result: BankCardResDto[] = [];

  const _physicalCards = cards?.filter((it) => it.form === CardForm.PHYSICAL) || [];

  const inactivePhysicalCards = _physicalCards.filter(
    (it) => ![CardStatus.ACTIVE, CardStatus.UNACTIVATED].includes(it.cardStatus as CardStatus),
  );

  const unActivatedPhysicalCard = _physicalCards.find(
    (it) => it.cardStatus === CardStatus.UNACTIVATED,
  );

  const activePhysicalCards = _physicalCards.filter((card) => {
    if (card.cardStatus === CardStatus.ACTIVE) return true;

    if (unActivatedPhysicalCard?.id && card.reissuedToId === unActivatedPhysicalCard.id)
      return true;

    return false;
  });

  if (activePhysicalCards?.length) {
    result = result.concat(activePhysicalCards);
  }
  if (unActivatedPhysicalCard) {
    result.push(unActivatedPhysicalCard);
  }

  if (inactivePhysicalCards.length && !activePhysicalCards?.length) {
    //NOTE:::If there is at least one inactive card, and no active card, then the last inactive card should be displayed.
    let lastInactiveCard = inactivePhysicalCards?.slice(-1)[0];

    //NOTE:::User has active reissued card (expiration reason of reissue) and new inactive card => active card became expired and inactive one become canceled => show expired card
    const lastExpiredCardRelevantToLastInactiveCard = _physicalCards.find(
      (it) =>
        it.reissuedToId === lastInactiveCard.id &&
        it.cardStatus === CardStatus.TERMINATED &&
        it.cardFulfillmentStatus === CardFulfillmentStatus.ISSUED &&
        it.statusReason === CardStatusReason.EXP,
    );

    if (lastExpiredCardRelevantToLastInactiveCard) {
      lastInactiveCard = lastExpiredCardRelevantToLastInactiveCard;
    }

    result.unshift(lastInactiveCard);
  }

  return result;
};

export const getSortedAndFilteredVirtualBankCards = (cards: BankCardResDto[]) => {
  let result: BankCardResDto[] = [];
  const _virtualCards = cards?.filter((it) => it.form === CardForm.VIRTUAL) || [];

  const activeVirtualCards = _virtualCards?.filter(
    (it) => it.cardStatus === CardStatus.ACTIVE && !it.reissueReason,
  );
  const inactiveVirtualCards = _virtualCards?.filter((it) => it.cardStatus !== CardStatus.ACTIVE);
  if (activeVirtualCards?.length) {
    result = result.concat(activeVirtualCards);
  }

  if (inactiveVirtualCards.length && !activeVirtualCards?.length) {
    const lastInactiveCardCard = inactiveVirtualCards?.slice(-1)[0] || [];
    result.push(lastInactiveCardCard);
  }

  return result;
};

export const getSortedAndFilteredBankCards = (cards: BankCardResDto[], userId?: string) => {
  const filteredCards = userId
    ? (cards || []).filter((item) => item.ownerInfo?.userId === userId)
    : cards;
  const physicalCards = getSortedAndFilteredPhysicalBankCards(filteredCards);
  const virtualCards = getSortedAndFilteredVirtualBankCards(filteredCards);

  return [...physicalCards, ...virtualCards];
};

export const getSortedAndFilteredBusinessBankCards = (cards: BankCardResDto[], userId?: string) => {
  //NOTE:::Getting user ids with userId from parameter being on top
  const allUserIds = uniqBy(cards, "ownerInfo.userId")
    .map((item) => item.ownerInfo?.userId)
    .sort((a, b) => {
      if (!a || !b) return 0;
      return a === userId ? -1 : 0;
    });

  let result: BankCardResDto[] = [];

  allUserIds.forEach((userId) => {
    const filteredUserCards = getSortedAndFilteredBankCards(cards, userId);
    result = result.concat(filteredUserCards);
  }, []);

  return result;
};

export const getSortedAndFilteredPersonalBankCards = (cards: BankCardResDto[], userId?: string) => {
  return getSortedAndFilteredBankCards(cards);
  //NOTE::: ???return sortBy(cards, "ownerInfo.lastName");
};

export const isSyncteraAccountDto = (
  card: SyncteraAccountDto | GetPlaidBankAccountsResponseDto,
): card is SyncteraAccountDto => {
  return (
    (card as SyncteraAccountDto)?.accountNumber !== undefined ||
    (card as SyncteraAccountDto)?.bankRouting !== undefined
  );
};

export const mapSyncteraAccountsToPlaidAccount = (
  accounts: SyncteraAccountDto[],
): IMappedBankAccount[] => {
  if (!accounts?.length) return [];

  const serializedData = accounts.map((account) => {
    return {
      bankAccountId: account.id,
      isDefault: undefined,
      plaidAccountId: undefined,
      accountName: undefined,
      type: undefined,
      subType: undefined,
      verificationStatus: undefined,
      verifiedAt: account.createdAt,
      last4Digits: account.accountNumber.slice(-3),
      institution: undefined,
      userId: undefined,
      companyId: undefined,
      currentBalance: undefined,
      availableBalance: undefined,
      accessToken: undefined,
      renewLoginRequired: undefined,
      manualVerificationRequired: undefined,
      manualBankName: undefined,
      manualOwnerName: undefined,
      _accountType: PayDistributionType.PARTNER_ACCOUNT,
    } as IMappedBankAccount;
  });

  return serializedData;
};

export const getSerializedPlaidBankAccounts = (
  bankCards: GetPlaidBankAccountsResponseDto[],
): IMappedBankAccount[] => {
  return bankCards?.map?.((bankCard) => {
    return {
      ...bankCard,
      _accountType: PayDistributionType.EXTERNAL_ACCOUNT,
    } as IMappedBankAccount;
  });
};

export const isBankAccountVerified = (data?: GetPlaidBankAccountsResponseDto) => {
  return [
    BankAccountVerifyStatus.AUTOMATICALLY_VERIFIED,
    BankAccountVerifyStatus.MANUALLY_VERIFIED,
    BankAccountVerifyStatus.INSTANTLY_VERIFIED,
  ].includes(data?.verificationStatus as BankAccountVerifyStatus);
};

export const isPhysicalCardActiveOrUnactivated = (card: BankCardResDto) =>
  card.form === CardForm.PHYSICAL &&
  [CardStatus.UNACTIVATED, CardStatus.ACTIVE].includes(card.cardStatus as CardStatus);

export const isCardActiveOrUnactivated = (card: BankCardResDto) =>
  [CardStatus.UNACTIVATED, CardStatus.ACTIVE].includes(card.cardStatus as CardStatus);
